import { useState, useCallback, useRef, useEffect } from 'react';

// Heart Rate Service UUIDs
const HEART_RATE_SERVICE = 'heart_rate';
const HEART_RATE_MEASUREMENT = 'heart_rate_measurement';

const STORAGE_KEY = 'clarityos_bt_device_name';
const RECONNECT_DELAY_MS  = 3000;
const RECONNECT_MAX_TRIES = 5;

export const useBiofeedback = () => {
  const [isSupported, setIsSupported]       = useState(false);
  const [isConnecting, setIsConnecting]     = useState(false);
  const [isConnected, setIsConnected]       = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [deviceName, setDeviceName]         = useState(null);
  const [heartRate, setHeartRate]           = useState(null);
  const [rrIntervals, setRrIntervals]       = useState([]);
  const [hrv, setHrv]                       = useState(null);
  const [error, setError]                   = useState(null);

  const deviceRef         = useRef(null);
  const characteristicRef = useRef(null);
  const rrBufferRef       = useRef([]);
  const hrHistoryRef      = useRef([]);
  const reconnectTimerRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const mountedRef        = useRef(true);

  useEffect(() => {
    setIsSupported(!!navigator.bluetooth);
    return () => { mountedRef.current = false; };
  }, []);

  const calculateHRV = useCallback((intervals) => {
    if (intervals.length < 2) return null;
    let sumSquaredDiffs = 0;
    for (let i = 1; i < intervals.length; i++) {
      const diff = intervals[i] - intervals[i - 1];
      sumSquaredDiffs += diff * diff;
    }
    return Math.round(Math.sqrt(sumSquaredDiffs / (intervals.length - 1)));
  }, []);

  const parseHeartRate = useCallback((value) => {
    const flags  = value.getUint8(0);
    let   offset = 1;
    const is16Bit = flags & 0x01;
    const hr = is16Bit ? value.getUint16(offset, true) : value.getUint8(offset);
    offset += is16Bit ? 2 : 1;
    setHeartRate(hr);
    hrHistoryRef.current = [...hrHistoryRef.current.slice(-59), { hr, timestamp: Date.now() }];

    const hasRR = flags & 0x10;
    if (hasRR) {
      const newIntervals = [];
      while (offset + 1 < value.byteLength) {
        newIntervals.push(Math.round((value.getUint16(offset, true) / 1024) * 1000));
        offset += 2;
      }
      if (newIntervals.length > 0) {
        rrBufferRef.current = [...rrBufferRef.current.slice(-100), ...newIntervals];
        setRrIntervals([...rrBufferRef.current]);
        if (rrBufferRef.current.length >= 10) {
          setHrv(calculateHRV(rrBufferRef.current.slice(-30)));
        }
      }
    }
  }, [calculateHRV]);

  const handleValueChange = useCallback((event) => {
    parseHeartRate(event.target.value);
  }, [parseHeartRate]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const wireNotifications = useCallback(async (server) => {
    const service        = await server.getPrimaryService(HEART_RATE_SERVICE);
    const characteristic = await service.getCharacteristic(HEART_RATE_MEASUREMENT);
    characteristicRef.current = characteristic;
    await characteristic.startNotifications();
    characteristic.addEventListener('characteristicvaluechanged', handleValueChange);
  }, [handleValueChange]);

  // Forward-declare via ref so handleDisconnect can call it before it's defined
  const attemptSilentReconnectRef = useRef(null);

  const handleDisconnect = useCallback((unexpected = false) => {
    setIsConnected(false);
    setHeartRate(null);
    setHrv(null);
    setRrIntervals([]);
    rrBufferRef.current = [];

    if (unexpected && deviceRef.current) {
      // Keep deviceName and deviceRef for reconnect
      reconnectCountRef.current = 0;
      reconnectTimerRef.current = setTimeout(
        () => attemptSilentReconnectRef.current?.(),
        RECONNECT_DELAY_MS
      );
    } else {
      setDeviceName(null);
      hrHistoryRef.current = [];
      deviceRef.current = null;
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const attemptSilentReconnect = useCallback(async () => {
    if (!mountedRef.current) return;
    const device = deviceRef.current;
    if (!device) return;

    reconnectCountRef.current += 1;
    if (reconnectCountRef.current > RECONNECT_MAX_TRIES) {
      setIsReconnecting(false);
      setError('HR monitor disconnected. Tap "Connect HR Monitor" to reconnect.');
      return;
    }

    try {
      setIsReconnecting(true);
      const server = await device.gatt.connect();
      await wireNotifications(server);
      if (mountedRef.current) {
        setIsConnected(true);
        setDeviceName(device.name || 'Heart Rate Monitor');
        setIsReconnecting(false);
        setError(null);
        reconnectCountRef.current = 0;
      }
    } catch {
      const delay = Math.min(RECONNECT_DELAY_MS * reconnectCountRef.current, 15000);
      reconnectTimerRef.current = setTimeout(
        () => attemptSilentReconnectRef.current?.(),
        delay
      );
    }
  }, [wireNotifications]);

  // Keep ref in sync
  useEffect(() => {
    attemptSilentReconnectRef.current = attemptSilentReconnect;
  }, [attemptSilentReconnect]);

  const connect = useCallback(async () => {
    if (!navigator.bluetooth) {
      setError('Web Bluetooth is not supported in this browser.');
      return false;
    }
    clearReconnectTimer();
    setIsConnecting(true);
    setIsReconnecting(false);
    setError(null);

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HEART_RATE_SERVICE] }],
        optionalServices: ['battery_service'],
      });

      deviceRef.current = device;
      localStorage.setItem(STORAGE_KEY, device.name || '');
      device.addEventListener('gattserverdisconnected', () => handleDisconnect(true));

      const server = await device.gatt.connect();
      await wireNotifications(server);

      if (mountedRef.current) {
        setIsConnected(true);
        setDeviceName(device.name || 'Heart Rate Monitor');
        setIsConnecting(false);
        reconnectCountRef.current = 0;
      }
      return true;
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || 'Failed to connect to device.');
        setIsConnecting(false);
      }
      return false;
    }
  }, [handleDisconnect, wireNotifications, clearReconnectTimer]);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    if (characteristicRef.current) {
      try {
        characteristicRef.current.removeEventListener('characteristicvaluechanged', handleValueChange);
        if (deviceRef.current?.gatt?.connected) {
          characteristicRef.current.stopNotifications().catch(() => {});
        }
      } catch { /* ignore */ }
      characteristicRef.current = null;
    }
    if (deviceRef.current?.gatt?.connected) {
      try { deviceRef.current.gatt.disconnect(); } catch { /* ignore */ }
    }
    handleDisconnect(false);
  }, [handleValueChange, handleDisconnect, clearReconnectTimer]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
    };
  }, [clearReconnectTimer]);

  const getAverageHR = useCallback(() => {
    if (hrHistoryRef.current.length === 0) return null;
    return Math.round(hrHistoryRef.current.reduce((a, b) => a + b.hr, 0) / hrHistoryRef.current.length);
  }, []);

  const getHRHistory = useCallback(() => {
    return hrHistoryRef.current.map(item => ({
      hr: item.hr,
      time: new Date(item.timestamp).toLocaleTimeString(),
    }));
  }, []);

  return {
    isSupported, isConnecting, isConnected, isReconnecting,
    deviceName, heartRate, hrv, rrIntervals, error,
    connect, disconnect, getAverageHR, getHRHistory,
  };
};
